/**
 * Check if header is sticky or not
 * https://css-tricks.com/how-to-detect-when-a-sticky-element-gets-pinned/
 **/

import debounce from '../utils';

class StickyHeader extends HTMLElement {
    constructor() {
        super();

        this.hide = this.hide.bind(this);
        this.reveal = this.reveal.bind(this);
        this.reset = this.reset.bind(this);
        this.stick = this.stick.bind(this);
        this.bindEvents = this.bindEvents.bind(this);
        this.onScroll = debounce(this.onScroll.bind(this), 10); // add debounce
    }

    connectedCallback() {
        this.header = document.querySelector('.shopify-section-header');
        this.stickyOffset = this.header?.getBoundingClientRect().top + 40; // prevent bouncing header
        this.currentHeaderState = 'normal';
        this.isHeaderSticky = false;
        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.isRunning = false;
        this.hideOffset = 400; // delay hide header

        if (!this.header) { return; }

        this.bindEvents();
    }

    bindEvents() {
        this.hideHeaderOnScrollUp = () => this.preventReveal = true;

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScroll, false);

        if (this.header) {
            this.checkIfHeaderStick();
        }
    }

    onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (scrollTop <= 0) { // scrollTop can be negative because of IOs bounce
            this.reset();
        } else if (scrollTop > this.currentScrollTop && !this.isHeaderSticky) {
            this.stick();
        } else if (scrollTop > this.currentScrollTop && scrollTop > this.hideOffset && this.isHeaderSticky) {
            this.hide();
        } else if (scrollTop < this.currentScrollTop && this.isHeaderSticky) {
            if (!this.preventReveal) {
                this.reveal();
            } else {
                window.clearTimeout(this.isScrolling);
                this.isScrolling = setTimeout(() => {
                this.preventReveal = false;
                }, 66);

                this.hide();
            }
        } else if (!this.isHeaderSticky) {
            this.reset();
        }
        
        this.currentScrollTop = scrollTop;
    }

    stick() {
        this.header.classList.add('shopify-section-header-sticky');
    }

    hide() {
        if (this.isRunning || this.currentHeaderState === 'hidden') {
            return;
        }
        
        this.isRunning = true;
        this.header.classList.add('shopify-section-header-hidden');
        this.currentHeaderState = 'hidden';
        this.isRunning = false;
    }
    
    reveal() {
        if (this.isRunning || this.currentHeaderState === 'revealed') {
            return;
        }
        
        this.isRunning = true;
        this.header.classList.add('shopify-section-header-sticky', 'header-sticky-transition');
        this.header.classList.remove('shopify-section-header-hidden');
        this.currentHeaderState = 'revealed';
        this.isRunning = false;
    }

    reset() {
        if (this.isRunning || this.currentHeaderState === 'normal') {
            return;
        }
        
        this.isRunning = true;
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'header-sticky-transition');
        this.currentHeaderState = 'normal';
        this.isRunning = false;
    }

    checkIfHeaderStick() {
        const observer = new IntersectionObserver(
            ([e]) => this.isHeaderSticky = e.intersectionRatio < 1,
            { threshold: [1] }
        );
        observer.observe(this.header);
    }
}

customElements.define('w3-sticky-header', StickyHeader);
